import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams, } from "ag-grid-enterprise";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { FilterDef } from "../../../constants/FinanceModels";
import { formatCurrency, formatDate } from "../../../utils/FormatUtils";
import { getLocalGridState, setLocalGridState, } from "../../../utils/GridUtils";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import EmptyState from "../../../../../util/component/EmptyState";
import ColouredChip from "../../../component/ColouredChip";
import { Box, Grid, IconButton, Stack, Typography } from "@mui/material";
import { ArrowBackRounded } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { NumericSummary } from "../../../component/NumericSummary";
import { useAmexTransactionsRepository } from "../../../api/repositories/AmexTransactionsRepository";
import { STATE_SUMMARY_UPDATING } from "../../../constants/FinanceRecoil";
import { convertNetworkCode, convertTypology, getSummaryFields, TRANSACTION_STATUSES, TYPES } from "../constants/TransactionConstants";
import { STATE_ACTIVE_FILTERS, STATE_TABLE_UPDATING } from "../constants/TransactionRecoil";


const TransactionTable = () => {
    const UNIQUE_ID = "fnn-sdd-amex-transactions";

    const { t } = useTranslation("finance", { keyPrefix: "payments.amex.transactions.detail" });
    const navigate = useNavigate();
    const { id, name } = useParams();
    const { getTransactions, transactionSummary} = useAmexTransactionsRepository();
    const filterDefs = useRecoilValue(STATE_ACTIVE_FILTERS);
    const [updating, setUpdating] = useRecoilState(STATE_TABLE_UPDATING);

    const setSummaryUpdating = useSetRecoilState(STATE_SUMMARY_UPDATING);

    const gridRef = useRef<AgGridReact>(null);

    const [columnDefs] = useState<ColDef[]>([
        {
            field: 'type', headerName: t('table.type'), headerTooltip: t('table.type'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => t(convertTypology(TYPES, params.value))
        },
        {
            field: "card.networkCode",
            headerName: t("table.networkCode"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true
        },
        {
            field: 'card.networkCode', headerName: t('table.pan'), headerTooltip: t('table.pan'),
            minWidth: 250,
            unSortIcon: true,
            valueFormatter: params => convertNetworkCode(params.data)
        },
        {
            field: "card.expirationDate",
            headerName: t("table.expirationDate"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params: any) => formatDate(params.value),
        },
        {
            field: 'amount', headerName: t('table.amount'), headerTooltip: t('table.amount'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => formatCurrency(params.value, 'EUR', 0.01)
        },
        {
            field: "orderId",
            headerName: t("table.orderId"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true
        },
        {
            field: "authorizationId",
            headerName: t("table.authorizationId"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true
        },
        {
            field: "status",
            headerName: t("table.status"),
            minWidth: 150,
            unSortIcon: true,
            sortable: true,
            pinned: "right",
            lockPosition: "right",
            lockVisible: true,
            lockPinned: true,
            cellRendererSelector: (params) => {
                let status: string = params.data.status;
                let color = TRANSACTION_STATUSES.find(s => s.value === status)?.color?.split(".")[0];
                let tooltipText: string = params.data.errorReason;
                if (!color || color === "neutral") color = "default";
                return {
                    component: () => (
                        <ColouredChip name={status} color={color} tooltip={tooltipText} />
                    ),
                };
            },
        }
    ]);


    const toolbar = {
        renderInfo: () => (
            <Grid>
                <IconButton color={'primary'}>
                    {/*<ArrowBackRounded />*/}
                    <Typography variant={'body2'} color={'primary'} fontWeight={700} textTransform={'uppercase'}>
                        {t('arrowback.title', { name: name })}
                    </Typography>
                </IconButton>

                <Box marginLeft={1} marginTop={2}>
{                    <NumericSummary
                        query={() => transactionSummary(id, filterDefs)}
                        fields={getSummaryFields()}
                        i18nPath={'payments.amex.transactions.reports.counters'}
                    /> }
                </Box>
            </Grid>
        ),
        renderActions: () => <Stack direction={'row'} spacing={2}></Stack>,
    };


    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data["id"],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
            headerCheckboxSelection: true
        },
        noRowsOverlayComponentParams: {
            title: t("table.no-rows.title"),
            content: t("table.no-rows.content")
        },
    };

    useEffect(() => {
        gridRef.current?.api?.setServerSideDatasource(
            createDatasource(filterDefs, gridRef.current.api)
        );
        setSummaryUpdating(true);
    }, [filterDefs]);

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(
                createDatasource(filterDefs, gridRef.current.api)
            );
            setUpdating(false);
            setSummaryUpdating(true);
        }
    }, [updating]);

    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort =
                    params.request.sortModel.length === 0
                        ? null
                        : params.request.sortModel[0].colId;
                let order =
                    params.request.sortModel.length === 0
                        ? null
                        : params.request.sortModel[0].sort.toUpperCase();
                getTransactions(startRow, endRow - startRow, sort, order, id, filterDefs)
                    .then((response) => {
                        let result = response.result;

                        if (result["total-amount"] === 0) {
                            api.showNoRowsOverlay();
                        } else {
                            api.hideOverlay();
                        }

                        params.success({
                            rowData: result.content,
                            rowCount: result["total-amount"],
                        });
                    })
                    .catch(() => params.fail());
            },
        };
    };

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    function saveState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRef.current!.api.paginationGetPageSize(),
            cols: gridRef.current!.columnApi.getColumnState(),
        });
    }

    return (
        <>
            <AgGridCustomTable
                toolbar={toolbar}
                rowModelType="serverSide"
                paginationPageSize={getLocalGridState(UNIQUE_ID).rows}
                serverSideInfiniteScroll
                gridRef={gridRef}
                columnDefs={columnDefs}
                gridOptions={gridOptions}
                rowSelection={"multiple"}
                suppressRowClickSelection
                noRowsOverlayComponent={noRowsOverlayComponent}
                onGridReady={() => {
                    gridRef.current!.api.setServerSideDatasource(
                        createDatasource(filterDefs, gridRef.current!.api)
                    );

                    let gridState = getLocalGridState(UNIQUE_ID).cols;
                    if (gridState !== undefined) {
                        gridRef.current!.columnApi.applyColumnState({
                            state: gridState,
                            applyOrder: true,
                        });
                    }
                }}
                onColumnMoved={(e) => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnPinned={(e) => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnResized={(e) => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onSortChanged={(e) => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onPaginationChanged={() => {
                    if (gridRef.current!.api) {
                        setLocalGridState(UNIQUE_ID, {
                            rows: gridRef.current!.api?.paginationGetPageSize(),
                            cols: getLocalGridState(UNIQUE_ID).cols,
                        });
                    }
                }}
            ></AgGridCustomTable>


        </>
    );
};

export default TransactionTable;
